import { getMessagesWithNumber, sendSms } from "helpers/backend_helper"
import dayjs from "dayjs"
import PropTypes from "prop-types"
import React, { useEffect, useRef, useState, useMemo } from "react"
import { withTranslation } from "react-i18next"
import { Col, Row, Button } from "reactstrap"
import { showToast, defaultPageSizeOptions, stringSorter } from "utils/general"
import CustomRangePicker from "components/DatePickers/RangePicker"
import { Table as AntdTable, Input as AntdInput } from "antd"
import { tableDefaultHeight } from "constants/layout"

const { Search } = AntdInput

const TrackerSmsTab = props => {
  const [entries, setEntries] = useState([])
  const [inputMessage, setInputMessage] = useState("")
  const [refreshing, setRefreshing] = useState(false)
  const [sending, setSending] = useState(false)
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(7, "days"),
    dayjs(),
  ])
  const [searchText, setSearchText] = useState("")
  const [filteredData, setFilteredData] = useState([])

  const abortRef = useRef(null)
  useEffect(() => {
    abortRef.current = new AbortController()
    return () => abortRef.current.abort()
  }, [])

  useEffect(() => {
    getMessages()
  }, [props.gsm, dateRange])

  // Handle search
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      const lower = searchText.toLowerCase()
      const filtered = entries.filter(
        item =>
          item.message?.toLowerCase().includes(lower) ||
          item.recipient?.toLowerCase().includes(lower) ||
          item.sender?.toLowerCase().includes(lower) ||
          item.source?.toLowerCase().includes(lower) ||
          item.status?.toLowerCase().includes(lower) ||
          item.statusTimeNice?.toLowerCase().includes(lower)
      )
      setFilteredData(filtered)
    }, 300)
    return () => clearTimeout(delayDebounce) // clear old timeout
  }, [searchText, dateRange, entries])

  // Get SMS entries
  const getMessages = () => {
    setEntries([])
    if (props.gsm?.length > 0 && dateRange && !refreshing) {
      //console.log("Get SMS: ", dateRange)
      setRefreshing(true)
      getMessagesWithNumber(
        abortRef.current,
        props.gsm,
        dateRange[0].valueOf(),
        dateRange[1].valueOf()
      )
        .then(data => {
          console.log("SMS: ", data)
          if (data.entries) {
            for (const element of data.entries) {
              element.statusTimeNice = dayjs(element.statusTime).format(
                "DD.MM.YYYY HH:mm:ss"
              )
              if (element.sent)
                element.sentNice = dayjs(element.sent).format(
                  "DD.MM.YYYY HH:mm:ss"
                )
            }
            setEntries(data.entries)
          }
        })
        .catch(e => {
          console.log(e)
          setEntries([])
        })
        .finally(() => {
          setRefreshing(false)
        })
    }
  }

  // Send SMS to tracker
  const sendMessage = () => {
    if (
      inputMessage.length > 0 &&
      props.gsm &&
      props.serverNumber &&
      !sending
    ) {
      setSending(true)
      sendSms(
        abortRef.current,
        inputMessage,
        props.gsm,
        props.serverNumber,
        null,
        null
      )
        .then(response => {
          console.log("Send response: ", response)
          showToast(props.t("Message sent"), "success")
          setDateRange([dateRange[0], dayjs()])
        })
        .catch(e => {
          showToast(props.t("Error"), "error")
          console.log(e)
        })
        .finally(() => {
          setSending(false)
        })
    }
  }

  const columns = useMemo(
    () => [
      {
        dataIndex: "statusTimeNice",
        title: props.t("Updated"),
        sorter: (a, b, sortOrder) => {
          return stringSorter(a, b, "statusTimeNice", sortOrder)
        },
        width: "160px",
        ellipsis: true,
      },
      {
        dataIndex: "status",
        title: props.t("Status"),
        sorter: (a, b, sortOrder) => {
          return stringSorter(a, b, "status", sortOrder)
        },
        width: "120px",
        ellipsis: true,
      },
      {
        dataIndex: "message",
        title: props.t("Message"),
        sorter: (a, b, sortOrder) => {
          return stringSorter(a, b, "message", sortOrder)
        },
        width: "400px",
        ellipsis: true,
      },
      {
        dataIndex: "sender",
        title: props.t("Sender"),
        sorter: (a, b, sortOrder) => {
          return stringSorter(a, b, "sender", sortOrder)
        },
        width: "180px",
        ellipsis: true,
      },
      {
        dataIndex: "recipient",
        title: props.t("Recipient"),
        sorter: (a, b, sortOrder) => {
          return stringSorter(a, b, "recipient", sortOrder)
        },
        width: "180px",
        ellipsis: true,
      },
      {
        dataIndex: "source",
        title: props.t("Source"),
        sorter: (a, b, sortOrder) => {
          return stringSorter(a, b, "source", sortOrder)
        },
        width: "180px",
        ellipsis: true,
      },
    ],
    [props.t]
  )

  const onDateSelection = value => {
    setDateRange(value)
  }

  return (
    <>
      <Row className="pb-3">
        <Col>
          <AntdInput
            allowClear
            placeholder={props.t("Message")}
            value={inputMessage}
            onChange={(v) => { setInputMessage(v.target.value) }}
          />
        </Col>
        <Col className="col-auto">
          <Button
            color="primary"
            className="compact-button"
            disabled={
              sending || !props.gsm || !props.serverNumber || !inputMessage
            }
            onClick={() => {
              sendMessage()
            }}
          >
            {props.t("Send")}
          </Button>
        </Col>
      </Row>
      <Row>
        <Col className="col-12">
          <React.Fragment>
            <Row className="mb-1">
              <Col className="col-auto mb-3">
                <Search
                  placeholder={props.t("Search")}
                  allowClear
                  value={searchText}
                  onChange={e => setSearchText(e.target.value)}
                  style={{ width: "180px" }}
                />
              </Col>

              <Col>
                <CustomRangePicker
                  value={dateRange}
                  rangeChanged={onDateSelection}
                  showTime={true}
                  allowClear={false}
                />
              </Col>

              <Col className="col-auto">
                <Button
                  className="d-flex align-items-center"
                  style={{ height: "31px" }}
                  color="primary"
                  disabled={refreshing}
                  onClick={() => {
                    setDateRange([dateRange[0], dayjs()])
                  }}
                >
                  <i className="mdi mdi-calendar-refresh me-2" />
                  {props.t("Refresh time")}
                </Button>
              </Col>

              <Col>
                <div className="mt-2 float-end">
                  <p>
                    {entries.length} {props.t("found results")}
                  </p>
                </div>
              </Col>
            </Row>
            <Row>
              <AntdTable
                size="small"
                className="colored-header narrow-header"
                columns={columns}
                dataSource={filteredData}
                pagination={{
                  showSizeChanger: true,
                  defaultPageSize: 50,
                  pageSizeOptions: defaultPageSizeOptions,
                }}
                tableLayout="auto"
                scroll={{
                  x: "max-content",
                  y: tableDefaultHeight,
                }}
              />
            </Row>
          </React.Fragment>
        </Col>
      </Row>
    </>
  )
}

TrackerSmsTab.propTypes = {
  t: PropTypes.any,
  gsm: PropTypes.string,
  serverNumber: PropTypes.string,
}

export default withTranslation()(TrackerSmsTab)
